import { useAuth } from "@/hooks/useAuth"
import type { User } from "@shared/schema"

export interface AccessControlResult {
  hasAccess: boolean
  reason?: string
  errorCode?: string
  isAdmin: boolean
  subscriptionStatus?: string
  subscriptionTier?: string
  upgradeRequired?: boolean
  premiumRequired?: boolean
}

export function useAccessControl() {
  const { user, isAuthenticated } = useAuth()

  const checkAccess = (requirePremium = false): AccessControlResult => {
    // Debug logging to understand what's happening
    console.log("🔍 Access Control Debug:", {
      isAuthenticated,
      user: user ? {
        id: (user as any).id,
        email: (user as any).email,
        role: (user as any).role,
        subscriptionStatus: (user as any).subscriptionStatus,
        accessEnabled: (user as any).accessEnabled
      } : null
    })

    // Not authenticated
    if (!isAuthenticated || !user) {
      console.log("🚫 Access denied: Not authenticated")
      return {
        hasAccess: false,
        reason: "Anmeldung erforderlich",
        errorCode: "AUTH_REQUIRED",
        isAdmin: false
      }
    }

    const userData = user as User

    // Admin users always have access
    if (userData.role === 'admin') {
      return {
        hasAccess: true,
        isAdmin: true
      }
    }

    // Check if access is explicitly disabled
    if (!userData.accessEnabled) {
      return {
        hasAccess: false,
        reason: "Ihr Zugang wurde deaktiviert. Bitte kontaktieren Sie den Support.",
        errorCode: "ACCOUNT_DISABLED",
        isAdmin: false,
        subscriptionStatus: userData.subscriptionStatus || undefined,
        subscriptionTier: userData.subscriptionTier || undefined
      }
    }

    // Check subscription status
    const now = new Date()
    const isSubscriptionValid = checkSubscriptionValidity(userData, now)

    if (!isSubscriptionValid) {
      return {
        hasAccess: false,
        reason: "Aktives Abonnement erforderlich für diese Funktion",
        errorCode: "SUBSCRIPTION_REQUIRED",
        isAdmin: false,
        subscriptionStatus: userData.subscriptionStatus || 'none',
        subscriptionTier: userData.subscriptionTier || 'basic',
        upgradeRequired: true
      }
    }

    // Check premium requirement
    if (requirePremium && userData.subscriptionTier !== 'premium') {
      return {
        hasAccess: false,
        reason: "Premium-Abonnement erforderlich für diese Funktion",
        errorCode: "PREMIUM_REQUIRED",
        isAdmin: false,
        subscriptionStatus: userData.subscriptionStatus || undefined,
        subscriptionTier: userData.subscriptionTier || undefined || 'basic',
        premiumRequired: true
      }
    }

    // User has valid access
    return {
      hasAccess: true,
      isAdmin: false,
      subscriptionStatus: userData.subscriptionStatus || undefined,
      subscriptionTier: userData.subscriptionTier || undefined
    }
  }

  return { checkAccess }
}

/**
 * Check if user has a valid subscription
 */
function checkSubscriptionValidity(user: User, now: Date): boolean {
  // If subscription status is active, check expiry
  if (user.subscriptionStatus === 'active') {
    // If no expiry date set, assume unlimited
    if (!user.subscriptionExpiresAt) {
      return true
    }
    // Check if subscription hasn't expired
    return new Date(user.subscriptionExpiresAt) > now
  }

  // Trial users get access if not expired
  if (user.subscriptionStatus === 'trial') {
    if (!user.subscriptionExpiresAt) {
      return true // Trial with no expiry
    }
    return new Date(user.subscriptionExpiresAt) > now
  }

  // Expired, cancelled, or no subscription = no access
  return false
}